import React, {
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import 'wax-prosemirror-core/dist/index.css';
import 'wax-prosemirror-services/dist/index.css';
import 'wax-table-service/dist/index.css';
import styled, { css, ThemeProvider } from 'styled-components';
import { saveAs } from "file-saver";
import {
  WaxContext,
  ComponentPlugin,
  DocumentHelpers,
  WaxView
} from 'wax-prosemirror-core';
import { grid, th } from '@pubsweet/ui-toolkit';
import { cokoTheme } from '../theme';
import EditorElements from './EditorElements';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Col, Row } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { CustomButton, showToaster, toasterTypes } from '../../../widgets';
import { postRevisedData } from '../../documents/apiController';
import ModalComponent from '../../common/Modal/modal';
import { PopUpFormElements } from './FormElements';
import { FieldItem } from '../../../widgets/fields';
import useFieldItem from '../../../widgets/fields/UseFieldItem';
import { updateSubmissionInfo } from './apiController';
import { Document, Packer, Paragraph, TextRun, ImageRun, Table, TableRow, TableCell, WidthType } from "docx";
// import Bibliography from '../components/Bibliography';


const divider = css`
  .panelGroup {
    background: #fff;
  }
  .divider {
    > div {
      background: ${th('colorBorder')};
      height: ${grid(1)};
      max-height: ${grid(1)};

      &:hover {
        height: ${grid(2)};
        max-height: ${grid(2)};
      }
    }
  }
`;

const Wrapper = styled.div`
  background: ${th('colorBackground')};
  font-family: ${th('fontInterface')};
  font-size: ${th('fontSizeBase')};
  line-height: ${grid(4)};
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow-y: hidden;

  ${divider}

  * {
    box-sizing: border-box;
  }
`;

const Main = styled.div`
  display: flex;
  flex: 1;
  overflow-y: auto;
  margin-top: 130px;
  flex-direction: row;

  @media (max-width: 768px) {
    margin-top: 180px;
  }

  @media (max-width: 480px) {
    margin-top: 220px;
  }
`;


const TopHead = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: fixed;
  z-index: 1000;
  background: var(--primary-color, #006BD9);
  border-top: ${th('borderWidth')} ${th('borderStyle')} ${th('colorBorder')};
  border-bottom: ${th('borderWidth')} ${th('borderStyle')} ${th('colorBorder')};
`;

const TopMenu = styled.div.attrs({ className: 'TopMenu' })`
  display: flex;
  flex-wrap: wrap;
  min-height: 40px;
  user-select: none;
  background: ${th('colorBackgroundToolBar')};
  border-top: ${th('borderWidth')} ${th('borderStyle')} ${th('colorBorder')};
  border-bottom: ${th('borderWidth')} ${th('borderStyle')} ${th('colorBorder')};
  padding: 0 20px;

  > div:not(:last-child) {
    border-right: ${th('borderWidth')} ${th('borderStyle')}
      ${th('colorFurniture')};
  }

  @media (min-width: 769px) {
    > div:nth-last-of-type(-n + 2) {
      margin-left: auto;
    }
  }

  > div:last-child {
    margin-left: 0;
    margin-right: 10px;
  }

  > div[data-name='Tables'] {
    border-right: none;
  }
`;

const SideMenu = styled.div`
  background: ${th('colorBackgroundToolBar')}
  border-right: ${th('borderWidth')} ${th('borderStyle')} ${th('colorBorder')};
  min-width: 250px;
  height: calc(100vh - 16px);
  position:fixed;
  left:0px;
`;

const EditorArea = styled.div`
  flex-grow: 1;
`;

const WaxSurfaceScroll = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 80%;
  height: 100%;
  position: absolute;
  left: 20vw;
  
  /* PM styles for main content */
  ${EditorElements};

  /* Custom scrollbar for WebKit browsers (Chrome, Safari) */
  ::-webkit-scrollbar {
    width: 12px; /* Set width of the vertical scrollbar */
  }

  ::-webkit-scrollbar-track {
    background: #fff; /* Background color of the scrollbar track */
  }

  ::-webkit-scrollbar-thumb {
    background-color: #fff; /* Color of the scrollbar thumb */
    border-radius: 10px;    /* Rounded corners for the thumb */
    border: 3px solid #fff; /* Optional: add a border to thumb */
  }

  ::-webkit-scrollbar-thumb:hover {
    background-color: #fff; /* Darker color on hover */
  }

  /* Custom scrollbar for Firefox */
  scrollbar-width: thin; /* Make the scrollbar thin */
  scrollbar-color: #fff #fff; /* Thumb color and track background */

  /* Optional: Add padding or margins if needed for scrollable content */
  padding: 16px;
`;


const WaxSurfaceScroll1 = styled.div`
  overflow-y: auto;
  display: flex;
  box-sizing: border-box;
  height: 100%;
  min-width: 250px;
  z-index:1;
 position:fixed;
  right:2vw;  /* PM styles for main content */
  ${EditorElements};

  /* Custom scrollbar for WebKit browsers (Chrome, Safari) */
  ::-webkit-scrollbar {
    width: 12px; /* Set width of the vertical scrollbar */
  }

  ::-webkit-scrollbar-track {
    background: #fff; /* Background color of the scrollbar track */
  }

  ::-webkit-scrollbar-thumb {
    background-color: #fff; /* Color of the scrollbar thumb */
    border-radius: 10px;    /* Rounded corners for the thumb */
    border: 3px solid #fff; /* Optional: add a border to thumb */
  }

  ::-webkit-scrollbar-thumb:hover {
    background-color: #fff; /* Darker color on hover */
  }

  /* Custom scrollbar for Firefox */
  scrollbar-width: thin; /* Make the scrollbar thin */
  scrollbar-color: #fff #fff; /* Thumb color and track background */

  /* Optional: Add padding or margins if needed for scrollable content */
  padding: 16px;
`;

const EditorContainer = styled.div`
  width: 80%;
  height: 100%;
  .ProseMirror {
    box-shadow: 0 0 8px #ecedf1;
    min-height: 98%;
    padding: ${grid(10)};
    overflow:auto;
  }
`;

const CommentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 300px;
  height: 100%;
`;

const CommentsContainerNotes = styled.div`
  display: flex;
  flex-direction: column;
  width: 20%;
  height: 100%;
`;

const CommentTrackToolsContainer = styled.div`
  display: flex;
  position: fixed;
  padding-top: 5px;
  right: 30px;
  z-index: 1;
  background: white;
  width: 10%;
`;

const CommentTrackTools = styled.div`
  margin-left: auto;
  display: flex;
  position: relative;
  z-index: 1;
`;

const CommentTrackOptions = styled.div`
  display: flex;
  margin-left: 10px;
  bottom: 5px;
  position: relative;
`;

const NotesAreaContainer = styled.div`
  background: #fff;
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  position: absolute;
  /* PM styles  for note content*/
  .ProseMirror {
    display: inline;
  }
`;

const NotesContainer = styled.div`
  counter-reset: footnote-view;
  display: flex;
  flex-direction: column;
  padding-top: 10px;
  padding-bottom: ${grid(4)};
  padding-left: ${grid(10)};
  height: 100%;
  width: 65%;
  ${EditorElements};
`;
const WaxBottomRightInfo = styled.div``;
const InfoContainer = styled.div`
  display: flex;
  position: fixed;
  bottom: 1px;
  right: 21px;
  z-index: 999;
`;

let surfaceHeight = (window.innerHeight / 5) * 3;
let notesHeight = (window.innerHeight / 5) * 2;

const onResizeEnd = arr => {
  surfaceHeight = arr[0].size;
  notesHeight = arr[1].size;
};

const getNotes = main => {
  const notes = DocumentHelpers.findChildrenByType(
    main.state.doc,
    main.state.schema.nodes.footnote,
    true,
  );
  return notes;
};

// const LeftSideBar = ComponentPlugin('leftSideBar');
const TopBar = ComponentPlugin('topBar');
const NotesArea = ComponentPlugin('notesArea');
const OutlineArea = ComponentPlugin('outlineArea');
const TextTypoSection = ComponentPlugin('textTypoSection');
const TableStyle = ComponentPlugin('customTableCss');
const VersionConfig = ComponentPlugin('versionSection');
const RightArea = ComponentPlugin('rightArea');
const CommentTrackToolBar = ComponentPlugin('commentTrackToolBar');
const BottomRightInfo = ComponentPlugin('BottomRightInfo');

const EditoriaLayout = (props, { editor }) => {
  const {
    pmViews: { main },
    activeView,
    options,
  } = useContext(WaxContext);

  let fullScreenStyles = {};

  if (options.fullScreen) {
    fullScreenStyles = {
      backgroundColor: '#fff',
      height: '100%',
      left: '0',
      margin: '0',
      padding: '0',
      position: 'fixed',
      top: '0',
      width: '100%',
      zIndex: '99999',
    };
  }
  const notes = main && getNotes(main);
  const commentsTracksCount =
    main && DocumentHelpers.getCommentsTracksCount(main);
  const trackBlockNodesCount =
    main && DocumentHelpers.getTrackBlockNodesCount(main);

  const areNotes = notes && !!notes.length && notes.length > 0;

  const [hasNotes, setHasNotes] = useState(areNotes);
  const [updateSubmissionPopUp, setUpdateSubmissionPopUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const initialValues = useMemo(() => (
    props.previousState ?
      props.previousState :
      { docType: "", submissionDate: "", submittedTo: "", remarks: "", submissionType: "" }
  ), [props.previousState]);
  const [values, fields, handleChange, { validateValues, updateValue }] = useFieldItem(PopUpFormElements, initialValues, { onValueChange });
  const navigate = useNavigate();
  const showNotes = () => {
    setHasNotes(areNotes);
  };

  const delayedShowedNotes = useCallback(
    setTimeout(() => showNotes(), 100),
    [],
  );

  useEffect(() => { }, [delayedShowedNotes]);
  const [docLength, setDocLength] = useState(0);
  const [project, setProject] = useState(JSON.parse(localStorage.getItem('docInfo')))
  // const users = [{
  //   userId: '1',
  //   displayName: 'test test',
  //   currentUser: true,
  // }]

  useEffect(() => {
    let docId = localStorage.getItem("documentSelected")
    if (docId) {
      let parsedJSON = JSON.parse(localStorage.getItem(docId));
      setDocLength(parsedJSON.content.length)
      console.log(parsedJSON.content.length)
    }

  }, [])

  /**
   * on save
   * create json format 
   */
  const onSaveDocument = async () => {
    let data = JSON.parse(localStorage.getItem('projectData'));
    let docId = localStorage.getItem("documentSelected")
    let parsedJSON = JSON.parse(localStorage.getItem(docId));
    let commentsData = JSON.parse(localStorage.getItem(docId + "_com"));

    console.log(commentsData)
    let Obj = {
      "topic_id": data.TopicID,
      "project_id": data.id,
      "doc_id": docId,
      "content": parsedJSON,
      "comment": commentsData
    }

    try {
      if (localStorage.getItem("isChanged") == "true") {
        localStorage.removeItem("isChanged")
        let saveDocResponse = await postRevisedData(Obj);
        if (saveDocResponse) {
          showToaster(toasterTypes.SUCCESS, "Document saved successfully!!")
        }
      }
    } catch (error) {
      throw error;
    }
  }


  /**
  * onclick documents
  */
  const onDocumentAction = async () => {
    if (localStorage.getItem("docEdit") != "false") {
      onSaveDocument();
    }
    navigate('/project', { state: { data: JSON.parse(localStorage.getItem('projectData')), type: "document" } })

  }

  const downloadDoc = () => {
    const { state } = activeView;
    //convertJsonToHTML(state.schema, "")
    exportToDocx(state.schema, "")

  }


  // Function to fetch image and convert to Base64
  const fetchImageAsBase64 = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]); // Extract Base64
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error fetching image:", error);
      return null;
    }
  };

    // 🔧 Utility function to extract text from a table cell
  const extractTextFromCell = (cell) => {
    if (!cell?.content) return "";
    return cell.content
      .map(paragraph =>
        paragraph?.content?.map(node => node?.text || "").join(" ") || ""
      )
      .join(" ");
  };

  // 🔧 Function to calculate dynamic column widths
  const calculateColumnWidths = (tableRows) => {
    const numCols = tableRows[0]?.content?.length || 1;
    const colLengths = Array(numCols).fill(0);

    tableRows.forEach(row => {
      row.content.forEach((cell, index) => {
        const textContent = extractTextFromCell(cell);
        colLengths[index] += textContent.trim().length;
      });
    });

    const total = colLengths.reduce((sum, len) => sum + len, 0);

    if (total === 0) return Array(numCols).fill(Math.floor(10000 / numCols));

    return colLengths.map(len => {
      const ratio = len / total;
      return Math.max(Math.floor(ratio * 10000), 1000); // At least 10% for any column
    });
  };

  /**
   * function to convert the schema to docx support format
   * @param {*} editorState 
   * @param {*} jsonContent 
   * @returns 
   */
const exportToDocx = async (editorState, jsonContent) => {
  if (!editorState) return;

  let docId = localStorage.getItem("documentSelected");
  let docData = JSON.parse(localStorage.getItem(docId));

  const docContent = docData.content;
  console.log(docContent)

  // Function to extract styles (text color, background color, etc.)
  const getTextRunStyles = (textNode) => {
    let textRunOptions = { text: textNode.text };

    if (textNode.marks) {
      textNode.marks.forEach(mark => {
        if (mark.type === "strong") textRunOptions.bold = true;
        if (mark.type === "em") textRunOptions.italics = true;
        if (mark.type === "underline") textRunOptions.underline = true;

        if (mark.type === "colorText" && mark.attrs.style) {
          const colorMatch = mark.attrs.style.match(/color:\s*#([0-9a-fA-F]{6})/);
          if (colorMatch) textRunOptions.color = colorMatch[1]; // Extract text color
        }

        if (mark.type === "bgColor" && mark.attrs.style) {
          const bgColorMatch = mark.attrs.style.match(/background-color:\s*#([0-9a-fA-F]{6})/);
          if (bgColorMatch) textRunOptions.shading = { fill: bgColorMatch[1] }; // Extract background color
        }
      });
    }

    return new TextRun(textRunOptions);
  };

  // Helper function to calculate maximum columns in a table
  const getMaxColumns = (tableRows) => {
    let maxCols = 0;
    tableRows.forEach(row => {
      let colCount = 0;
      row.content.forEach(cell => {
        colCount += cell.attrs.colspan || 1;
      });
      maxCols = Math.max(maxCols, colCount);
    });
    return maxCols;
  };

  // Helper function to normalize table rows
  const normalizeTableRows = (tableRows, maxColumns) => {
    return tableRows.map(row => {
      const normalizedCells = [...row.content];
      
      // Calculate current row's column count
      let currentColCount = 0;
      normalizedCells.forEach(cell => {
        currentColCount += cell.attrs.colspan || 1;
      });
      
      // Add empty cells if needed to match maxColumns
      while (currentColCount < maxColumns) {
        normalizedCells.push({
          attrs: {
            colspan: 1,
            colwidth: null,
            rowspan: 1,
            style: null
          },
          content: [
            {
              attrs: {
                class: "paragraph",
                group: "",
                id: "",
                track: [],
                viewid: ""
              },
              content: [
                {
                  text: "",
                  type: "text"
                }
              ],
              type: "paragraph"
            }
          ],
          type: "table_cell"
        });
        currentColCount++;
      }
      
      return { ...row, content: normalizedCells };
    });
  };

  const docxElements = await Promise.all(
    docContent && docContent.map(async (node) => {
      if (node.type === "title") {
        return new Paragraph({
          children: node.content?.map(getTextRunStyles),
          heading: "Title", // Title mapped to Heading1 in Word
          spacing: { before: 300, after: 200 }, // Margin-top
        });
      } else if (node.type === "orderedlist" || node.type === "bulletlist") {
        return node.content.map((listItem, index) => {
          const listText = listItem.content[0].content.map(textNode => textNode.text).join(" ");
          return new Paragraph({
            spacing: { before: 200, after: 200 },
            children: [
              new TextRun({
                text: node.type === "orderedlist" ? `${index + 1}. ${listText}` : `• ${listText}`
              })
            ],
          });
        });
      } else if (node.type === "heading2") {
        return new Paragraph({
          children: node.content?.map(getTextRunStyles),
          heading: "Heading2",
          spacing: { before: 300, after: 200 }, // Margin-top
        });
      } else if (node.type === "heading3") {
        return new Paragraph({
          children: node.content?.map(getTextRunStyles),
          heading: "Heading3",
          spacing: { before: 300, after: 200 }, // Margin-top
        });
      } else if (node.type === "paragraph") {
        return new Paragraph({
          spacing: { before: 300, after: 200 }, // Margin-top
          children: node.content?.map(getTextRunStyles).filter(Boolean), // Remove empty text runs
        });
      } else if (node.type === "table") {
        const tableBody = node.content.find(item => item.type === "table_body");
        if (!tableBody || !tableBody.content) return null;

        const maxColumns = getMaxColumns(tableBody.content);
        const normalizedRows = normalizeTableRows(tableBody.content, maxColumns);
        const columnWidths = Array(maxColumns).fill(Math.floor(10000 / maxColumns));

        return new Table({
          spacing: { before: 300, after: 200 },
          width: { size: 10000, type: WidthType.PERCENTAGE },
          columnWidths: columnWidths,
          rows: normalizedRows.map((row) =>
            new TableRow({
              children: row.content.map((cell) => {
                const cellChildren = (cell.content || []).map((childNode) => {
                  if (childNode.type === "paragraph") {
                    const textContent = (childNode.content || []).map(getTextRunStyles);
                    return new Paragraph({ children: textContent.length > 0 ? textContent : [new TextRun("")] });
                  } else if (childNode.type === "bulletlist" || childNode.type === "orderedlist") {
                    return childNode.content.map((li, idx) => 
                      new Paragraph({
                        bullet: childNode.type === "bulletlist" ? { level: 0 } : undefined,
                        numbering: childNode.type === "orderedlist" ? { reference: "ordered-list", level: 0 } : undefined,
                        children: [new TextRun(li.content[0].content.map(t => t.text).join(""))]
                      })
                    );
                  }
                  return null;
                }).flat().filter(Boolean);

                const colspan = cell.attrs.colspan || 1;
                return new TableCell({
                  width: { size: Math.floor(10000 / maxColumns) * colspan, type: WidthType.PERCENTAGE },
                  columnSpan: colspan > 1 ? colspan : undefined,
                  children: cellChildren.length > 0 ? cellChildren : [new Paragraph("")],
                  borders: {
                    top: { style: "single", size: 4, color: "000000" },
                    bottom: { style: "single", size: 4, color: "000000" },
                    left: { style: "single", size: 4, color: "000000" },
                    right: { style: "single", size: 4, color: "000000" },
                  },
                });
              }),
            })
          ),
        });
      } else if (node.type === "figure") {
        const imageNode = node.content && node.content.find((item) => item.type === "image");
        const captionNode = node.content && node.content.find((item) => item.type === "figcaption");

        if (imageNode) {
          const base64Image = await fetchImageAsBase64(imageNode.attrs.src);
          if (base64Image) {
            const byteArray = Uint8Array.from(atob(base64Image), (c) => c.charCodeAt(0));

            // Create an image to get natural dimensions
            const img = new Image();
            img.src = `data:image/jpeg;base64,${base64Image}`;
            
            return new Promise((resolve) => {
              img.onload = () => {
                const maxWidth = 600; // Maximum width in pixels
                const maxHeight = 400; // Maximum height in pixels
                
                let { width, height } = img;
                
                // Calculate aspect ratio and resize
                const aspectRatio = width / height;
                
                if (width > maxWidth) {
                  width = maxWidth;
                  height = width / aspectRatio;
                }
                
                if (height > maxHeight) {
                  height = maxHeight;
                  width = height * aspectRatio;
                }

                const imageParagraph = new Paragraph({
                  spacing: { before: 300, after: 200 },
                  alignment: "center",
                  children: [
                    new ImageRun({
                      data: byteArray,
                      transformation: { 
                        width: Math.round(width),
                        height: Math.round(height)
                      },
                    }),
                  ],
                });

                if (captionNode && captionNode.content?.[0]?.text) {
                  const captionParagraph = new Paragraph({
                    alignment: "center",
                    spacing: { before: 100, after: 200 },
                    children: [new TextRun({ text: captionNode.content[0].text, italics: true, size: 20 })],
                  });
                  resolve([imageParagraph, captionParagraph]);
                } else {
                  resolve(imageParagraph);
                }
              };
              
              img.onerror = () => {
                // Fallback if image loading fails
                const imageParagraph = new Paragraph({
                  spacing: { before: 300, after: 200 },
                  alignment: "center",
                  children: [
                    new ImageRun({
                      data: byteArray,
                      transformation: { width: 500, height: 300 },
                    }),
                  ],
                });
                resolve(imageParagraph);
              };
            });
          }
        }
      }
      return null;
    })
  );

  //Create new document with the content created 
  const docxFile = new Document({
    sections: [{ properties: {}, children: docxElements.flat().filter(Boolean) }],
  });

  //Download the data
  const blob = await Packer.toBlob(docxFile);
  saveAs(blob, project && project.docInfo.name + '.docx');
};


  /**
   * function to convert json to HTML 
   * @param {schema} st this 
   * @param {jsonContent} jsonContent 
   * @returns {Promise}
   * @description function to convert json to HTML and download as docx file
   * @author Sachin
   * @date 2024-11-09
   * commented the code Sachin
   */
  // const convertJsonToHTML = async (st, jsonContent) => {
  //   let docId = localStorage.getItem("documentSelected");
  //   let docData = JSON.parse(localStorage.getItem(docId));
  //   jsonContent = docData;
  //   // Convert the JSON to a ProseMirror Node
  //   const { state, dispatch } = activeView;

  //   const { schema } = state.schema;
  //   console.log(activeView)
  //   const prosemirrorDoc = st.nodeFromJSON(jsonContent);

  //   // Create a DOMSerializer
  //   const serializer = DOMSerializer.fromSchema(st);

  //   // Serialize ProseMirror document to HTML
  //   const fragment = serializer.serializeFragment(prosemirrorDoc.content);

  //   // Convert the DOM fragment to an HTML string
  //   const div = document.createElement("div");
  //   div.appendChild(fragment);
  //   const htmlOutput = div.innerHTML;
  //   console.log(htmlOutput)
  //   let replaceBase64 = await replaceImageUrlsWithBase64(htmlOutput)
  //   console.log(replaceBase64)

  //   const converted = htmlDocx.asBlob(`<!DOCTYPE html><html><body>${htmlOutput}</body></html>`);
  //   console.log(converted)
  //   saveAs(converted, "document.docx");

  //   // try {
  //   //   let response = await postDownloadDocx(replaceBase64)

  //   //   //convert blob to object url and download
  //   //   const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
  //   //   const url = URL.createObjectURL(blob);
  //   //   const link = document.createElement('a');
  //   //   link.href = url;
  //   //   link.download = project && project.docInfo.name + '.docx';
  //   //   link.click();
  //   //   URL.revokeObjectURL(url);
  //   // } catch (error) {
  //   //   console.log("DOcumet download Error:::::", error)
  //   // }
  // }

  function onValueChange(name, value, values, fieldValues = null) {
    let resetValue = {};
    return [{
      ...values,
      ...resetValue,
      [name]: value,
    }]
  }

  async function submitUpdateSubmissionInfo() {
    if (validateValues(["submissionDate", "submittedTo", "remarks", "submissionType"])) {
      showToaster(toasterTypes.ERROR, "Please enter the proper values in the fields highlighted in red");
    }
    else {
      let docId = localStorage.getItem("documentSelected")
      let parsedJSON = JSON.parse(localStorage.getItem(docId));
      let projectData = JSON.parse(localStorage.getItem('projectData'));
      console.log(projectData.TopicID)
      let reqObj = {
        "topic_id": projectData.TopicID,
        "submission_date": values.submissionDate.toISOString(),
        "submitted_to": values.submittedTo,
        "remarks": values.remarks,
        "submission_type": values.submissionType.value
      }
      console.log(reqObj)
      setIsLoading(true);
      try {
        let updateSubInfoResponse = await updateSubmissionInfo(reqObj, docId);
        console.log(updateSubInfoResponse)
        if (updateSubInfoResponse.success) {
          showToaster(toasterTypes.SUCCESS, "Submission Info Updated successfully!!")
        }
        setUpdateSubmissionPopUp(false);
      } catch (error) {
        showToaster(toasterTypes.ERROR, "Error while updating submission info!!")
        throw error;
      } finally {
        setIsLoading(false);
        setUpdateSubmissionPopUp(false);
      }
    }
  }

  return (
    <>
      {
        updateSubmissionPopUp &&
        <ModalComponent
          width={"350px"}
          isOpen={true}
          onClose={() => setUpdateSubmissionPopUp(false)}
          header={"Update Submission Info"}
          centered={true}
          customClass='template-modal'
          body={
            <div className="">
              <FieldItem
                {...PopUpFormElements.submissionType}
                value={values.submissionType}
                onChange={(...e) => handleChange(PopUpFormElements.submissionType.name, ...e)}
                touched={fields.submissionType && fields.submissionType.hasError}
                error={fields.submissionType && fields.submissionType.errorMsg}
              />
              <FieldItem
                {...PopUpFormElements.submissionDate}
                value={values.submissionDate}
                onChange={(...e) => handleChange(PopUpFormElements.submissionDate.name, ...e)}
                touched={fields.submissionDate && fields.submissionDate.hasError}
                error={fields.submissionDate && fields.submissionDate.errorMsg}
              />
              <FieldItem
                {...PopUpFormElements.submittedTo}
                value={values.submittedTo}
                onChange={(...e) => handleChange(PopUpFormElements.submittedTo.name, ...e)}
                touched={fields.submittedTo && fields.submittedTo.hasError}
                error={fields.submittedTo && fields.submittedTo.errorMsg}
              />
              <FieldItem
                {...PopUpFormElements.remarks}
                value={values.remarks}
                onChange={(...e) => handleChange(PopUpFormElements.remarks.name, ...e)}
                touched={fields.remarks && fields.remarks.hasError}
                error={fields.remarks && fields.remarks.errorMsg}
              />
            </div>
          }
          footer={
            <CustomButton text="Submit" type="primary" onClick={submitUpdateSubmissionInfo} disabled={isLoading}></CustomButton>
          }
        />
      }
      <ThemeProvider theme={cokoTheme}>

        <Wrapper style={fullScreenStyles} id="wax-container">
          <TopHead>
            <Row className='page-header m-0 align-items-center' >
              <Col className={window.innerWidth > 750 ? "project-title" : "project-title-mob"} xs={12} md={10}>
                <ArrowLeftOutlined style={{ color: "#fff", fontSize: "24px", margin: "0px 10px" }} onClick={() => onDocumentAction()} />
                <span style={{ color: "#fff", fontSize: "24px", margin: "0px 10px" }}>{project && project.docInfo.name}</span>

              </Col>
              <Col className='p-0 text-center text-md-right mt-2 mt-md-0' xs={6} md={1}>
                <span style={{ cursor: "pointer", color: "#fff", fontSize: "16px", margin: "0px 1px", border: "1px solid #fff", padding: "5px 10px", borderRadius: "5px" }} onClick={() => downloadDoc()} >Download</span>
              </Col>
              <Col className='p-0 text-center text-md-right mt-2 mt-md-0' xs={6} md={1}>
                <span style={{ cursor: "pointer", color: "#fff", fontSize: "16px", margin: "0px 1px", border: "1px solid #fff", padding: "5px 10px", borderRadius: "5px" }} onClick={() => setUpdateSubmissionPopUp(true)} >Update</span>
              </Col>
            </Row>

            <TopMenu>
              <VersionConfig />
              <TextTypoSection />
              <TableStyle />

              <TopBar />
            </TopMenu>
          </TopHead>
          <Main>
            <SideMenu>
              <OutlineArea />
              {/* <TextColor/> */}
            </SideMenu>
            <EditorArea>
              <WaxSurfaceScroll>
                <EditorContainer>
                  <WaxView {...props} />
                </EditorContainer>
              </WaxSurfaceScroll>
            </EditorArea>
            <EditorArea>
              {/* <PanelGroup
                direction="column"
                panelWidths={[
                  { size: surfaceHeight, resize: 'stretch' },
                  { size: notesHeight, resize: 'resize' },
                ]}
                onResizeEnd={onResizeEnd}
              > */}
              <WaxSurfaceScroll1>
                <EditorContainer>{editor}</EditorContainer>
                <CommentsContainer>
                  {/* <CommentTrackToolsContainer>
                    <CommentTrackTools>
                      {commentsTracksCount + trackBlockNodesCount} COMMENTS AND
                      SUGGESTIONS
                      <CommentTrackOptions>
                        <CommentTrackToolBar />
                      </CommentTrackOptions>
                    </CommentTrackTools>
                  </CommentTrackToolsContainer> */}
                  <RightArea area="main" />
                </CommentsContainer>
              </WaxSurfaceScroll1>

              {hasNotes && (
                <NotesAreaContainer>
                  <NotesContainer id="notes-container">
                    <NotesArea view={main} />
                  </NotesContainer>
                  <CommentsContainerNotes>
                    <RightArea area="notes" />
                  </CommentsContainerNotes>
                </NotesAreaContainer>
              )}
              {/* </PanelGroup> */}
            </EditorArea>
          </Main>
          <WaxBottomRightInfo>
            <InfoContainer id="info-container">
              <BottomRightInfo />
            </InfoContainer>

          </WaxBottomRightInfo>
        </Wrapper>
      </ThemeProvider>
    </>
  );
};

export default EditoriaLayout;
